/**
 * arbi task list                       List background tasks
 * arbi task status [task-id]           Poll and display current status
 * arbi task result [task-id]           Fetch and print completed result
 *
 * Management commands for background tasks submitted via `arbi ask -b`.
 * Task metadata is stored locally in ~/.arbi/tasks.json.
 * Status and results are fetched from the backend via GET /v1/responses/{id}.
 */

import { Command } from 'commander'
import { responses } from '@arbidocs/sdk'
import {
  runAction,
  resolveWorkspace,
  printTable,
  printJson,
  suggestSubcommandAndExit,
} from '../helpers.js'
import { getTasks, getLatestTask, updateTaskStatus } from '../task-store.js'
import { chalk } from '../output.js'

function formatAge(isoDate: string): string {
  const ms = Date.now() - new Date(isoDate).getTime()
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h`
  return `${Math.round(ms / 86_400_000)}d`
}

function resolveTaskId(taskIdArg?: string): string {
  if (taskIdArg) return taskIdArg
  const latest = getLatestTask()
  if (!latest) {
    console.error(chalk.red('No tasks found. Submit one with: arbi ask -b "your question"'))
    process.exit(1)
  }
  return latest.id
}

/** Shape we return from `task list --json`. Public contract — keep stable. */
interface TaskListEntry {
  id: string
  status: string
  question: string
  age_seconds: number
  submitted_at: string
}

function statusColor(s: string): string {
  if (s === 'completed') return chalk.green(s)
  if (s === 'failed') return chalk.red(s)
  if (s === 'queued' || s === 'in_progress') return chalk.yellow(s)
  return s
}

export function registerTaskCommand(program: Command): void {
  const task = program
    .command('task')
    .description('Background tasks (from `arbi ask -b`): list, status, result')

  // Default action: list, OR suggest a subcommand if user typed a typo.
  task.arguments('[maybeSubcommand]').action(async (maybe?: string) => {
    if (maybe) {
      suggestSubcommandAndExit(
        'task',
        maybe,
        task.commands.map((c) => c.name())
      )
    }
    await task.commands.find((c) => c.name() === 'list')!.parseAsync([], { from: 'user' })
  })

  task
    .command('list')
    .description('List background tasks')
    .option('--json', 'Output as JSON (recommended for scripting / agents)')
    .action((opts?: { json?: boolean }) =>
      runAction(async () => {
        const tasks = getTasks()
        if (opts?.json) {
          const out: TaskListEntry[] = tasks.map((t) => ({
            id: t.id,
            status: t.status,
            question: t.question,
            age_seconds: Math.round((Date.now() - new Date(t.submittedAt).getTime()) / 1000),
            submitted_at: t.submittedAt,
          }))
          printJson(out)
          return
        }
        if (tasks.length === 0) {
          // Empty-state hint stays useful for humans but goes to stderr so
          // `arbi task list | wc -l` returns 0, not 1.
          process.stderr.write('No tasks. Submit one with: arbi ask -b "your question"\n')
          return
        }

        printTable(
          [
            { header: 'ID', width: 20, value: (r) => r.id as string },
            { header: 'STATUS', width: 14, value: (r) => statusColor(r.status as string) },
            { header: 'QUESTION', width: 42, value: (r) => r.question as string },
            { header: 'AGE', width: 6, value: (r) => formatAge(r.submittedAt as string) },
          ],
          tasks as unknown as Record<string, unknown>[]
        )
      })()
    )

  task
    .command('status [task-id]')
    .description('Check current task status (defaults to most recent)')
    .option('-w, --workspace <id>', 'Workspace ID')
    .option('--json', 'Output in JSON format')
    .action((taskId?: string, opts?: { workspace?: string; json?: boolean }) =>
      runAction(async () => {
        const id = resolveTaskId(taskId)
        const { accessToken, config } = await resolveWorkspace(opts?.workspace)

        const result = await responses.getResponse({ baseUrl: config.baseUrl, accessToken }, id)

        if (result.status === 'completed' || result.status === 'failed') {
          updateTaskStatus(id, result.status)
        }

        if (opts?.json) {
          console.log(JSON.stringify(result, null, 2))
        } else {
          console.log(`${chalk.bold('ID:')}     ${result.id}`)
          console.log(`${chalk.bold('Status:')} ${statusColor(result.status)}`)
          if (result.model) console.log(`${chalk.bold('Model:')}  ${result.model}`)
          if (result.usage) {
            console.log(`${chalk.bold('Tokens:')} ${result.usage.total_tokens.toLocaleString()}`)
          }
        }
      })()
    )

  task
    .command('result [task-id]')
    .description('Fetch and print the completed task result')
    .option('-w, --workspace <id>', 'Workspace ID')
    .option('--json', 'Output in JSON format')
    .action((taskId?: string, opts?: { workspace?: string; json?: boolean }) =>
      runAction(async () => {
        const id = resolveTaskId(taskId)
        const { accessToken, config } = await resolveWorkspace(opts?.workspace)

        const result = await responses.getResponse({ baseUrl: config.baseUrl, accessToken }, id)

        if (result.status === 'completed' || result.status === 'failed') {
          updateTaskStatus(id, result.status)
        }

        if (opts?.json) {
          console.log(JSON.stringify(result, null, 2))
          return
        }

        if (result.status !== 'completed') {
          console.error(
            chalk.yellow(`Task is ${result.status}. Use \`arbi task status\` to check progress.`)
          )
          process.exit(1)
        }

        const text = responses.extractResponseText(result)
        process.stdout.write(text + '\n')
        if (result.usage) {
          console.error(chalk.dim(`[${result.usage.total_tokens.toLocaleString()} tokens]`))
        }
      })()
    )
}
